import { nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";

// Interface for filter configuration
interface FilterConfig {
  value: string | number | boolean | undefined | null;
  acceptedValues: string | string[] | "boolean";
  label?: string;
}

interface SearchFilter {
  [key: string]: FilterConfig;
}

export function useUrlFilters(searchFilter: SearchFilter) {
  const route = useRoute();
  const router = useRouter();

  // Function to serialize filter values to URL format
  const serializeFilters = (filters: SearchFilter): Record<string, string> => {
    const result: Record<string, string> = {};

    Object.keys(filters).forEach(key => {
      const filter = filters[key];
      if (
        filter.value !== undefined &&
        filter.value !== null &&
        filter.value !== ""
      ) {
        // Convert boolean to string for URL
        if (typeof filter.value === "boolean") {
          result[key] = filter.value.toString();
        } else {
          result[key] = filter.value.toString();
        }
      }
    });

    return result;
  };

  // Function to deserialize URL params to filter values
  const deserializeFilters = (
    params: Record<string, string>,
    filterConfig: SearchFilter
  ): SearchFilter => {
    const result = JSON.parse(JSON.stringify(filterConfig)); // Deep copy

    Object.keys(result).forEach(key => {
      if (params[key] !== undefined) {
        const filter = result[key];
        const paramValue = params[key];

        // Handle different filter types
        if (filter.acceptedValues === "boolean") {
          result[key].value = paramValue === "true";
        } else if (filter.acceptedValues === "string") {
          result[key].value = paramValue;
        } else if (Array.isArray(filter.acceptedValues)) {
          // For select options, ensure the value exists in the accepted values
          if (filter.acceptedValues.includes(paramValue)) {
            result[key].value = paramValue;
          }
        }
      }
    });

    return result;
  };

  // Function to update URL with current filter values
  const updateUrl = (filters: SearchFilter, replace = false) => {
    const query = { ...route.query };
    const serializedFilters = serializeFilters(filters);

    // Remove existing filter keys from query
    Object.keys(searchFilter).forEach(key => {
      delete query[key];
    });

    // Add current filter values
    Object.assign(query, serializedFilters);

    // Update URL
    const newRoute = { ...route, query };
    if (replace) {
      router.replace(newRoute);
    } else {
      router.push(newRoute);
    }
  };

  // Function to initialize filters from URL
  const initializeFiltersFromUrl = (): SearchFilter => {
    const urlParams = route.query as Record<string, string>;
    return deserializeFilters(urlParams, searchFilter);
  };

  // Function to clear all filters and update URL
  const clearFilters = (filters: SearchFilter) => {
    const clearedFilters = JSON.parse(JSON.stringify(filters));
    Object.keys(clearedFilters).forEach(key => {
      clearedFilters[key].value = undefined;
    });

    updateUrl(clearedFilters, true);
    return clearedFilters;
  };

  // Function to update a single filter value and URL
  const updateFilterValue = (
    filters: SearchFilter,
    key: string,
    value: string | number | boolean | undefined | null
  ): SearchFilter => {
    const updatedFilters = { ...filters };
    updatedFilters[key] = { ...updatedFilters[key], value };

    // Debounce URL updates for string inputs to avoid too many URL changes
    nextTick(() => {
      updateUrl(updatedFilters, true);
    });

    return updatedFilters;
  };

  return {
    initializeFiltersFromUrl,
    updateUrl,
    clearFilters,
    updateFilterValue,
    serializeFilters,
    deserializeFilters
  };
}
